import dayjs, { Dayjs } from "dayjs";
import { isEmpty } from "lodash";

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FlightWayEnum } from "@/constants";
import { AirportMapItem } from "@/constants/airport";
import { MAX_PRO_DATE_RANGE } from "@/constants/config";
import { FREE_FLIGHT_RANGE, MAX_FLIGHT_RANGE } from "@/constants/membership";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { parseSearchQuery } from "@/utils/searchQuery";

export interface SearchConditionType {
  flightWay: string;
  pax: number;
  children: number;
  cabins: string;
  range: boolean;
  rangeV2: boolean;

  // airport
  fromLocation: AirportMapItem[];
  toLocation: AirportMapItem[];

  // one way date
  oneWayDate: number;
  oneWayRangeStartDate: number;
  oneWayRangeEndDate: number;

  // round trip date
  roundTripDepartureDate: number;
  roundTripReturnDate: number;

  // round trip one range date
  departureRangeStartDate: number;
  departureRangeEndDate: number;

  // round trip two range date
  returnRangeStartDate: number;
  returnRangeEndDate: number;

  // programs
  programs: string[];

  targetId?: string;
}

export interface SearchLimitType {
  flight_way: number;
  choice_count_dep: number;
  choice_count_arrival: number;
  daterange_dep: number;
  daterange_arrival: number;
  search_calendar_range: number;
}

const useSearchCondition = () => {
  const [searchParams] = useSearchParams();
  const initParams = useRef(parseSearchQuery(searchParams));
  const [searchLimit, setSearchLimit] = useState<SearchLimitType>({
    flight_way: 1,
    choice_count_dep: 4,
    choice_count_arrival: 4,
    daterange_dep: 3,
    daterange_arrival: 3,
    search_calendar_range: 365,
  });
  const [condition, setCondition] = useState<SearchConditionType>(() => {
    // let list: string[] = [];
    // try {
    //   list = (JSON.parse(localStorage.getItem("__FlightSearchPrograms__") || "") as string[]) || [];
    // } catch (error) {
    //   list = [];
    // }
    return {
      ...initParams.current,
      // programs: list,
    };
  });

  const store = useGlobalStoreContext();

  useEffect(() => {
    const value =
      (condition.flightWay === FlightWayEnum.OneWay && searchLimit.daterange_dep > 1) ||
      (condition.flightWay === FlightWayEnum.RoundTrip &&
        !(searchLimit.daterange_dep <= 1 && searchLimit.daterange_arrival <= 1));

    if (value !== condition.range) {
      setCondition({ ...condition, range: value });
    }
  }, [searchLimit.daterange_dep, searchLimit.daterange_arrival, condition, setCondition]);

  useEffect(() => {
    const max_entry = MAX_FLIGHT_RANGE;
    try {
      const freeUser = store?.user_type == 0;
      const maxEntry = freeUser
        ? condition.flightWay == FlightWayEnum.OneWay
          ? condition.rangeV2
            ? max_entry
            : FREE_FLIGHT_RANGE
          : condition.rangeV2
          ? max_entry
          : 2
        : max_entry;

      const entry_left = maxEntry / (condition.flightWay == FlightWayEnum.OneWay ? 1 : 1);

      let oneway_date_diff =
        condition.oneWayRangeEndDate == 0 || condition.oneWayRangeStartDate == 0
          ? 1
          : (condition.oneWayRangeEndDate - condition.oneWayRangeStartDate) / 86400000 + 1;
      oneway_date_diff = oneway_date_diff ? oneway_date_diff : 1;

      let depart_date_diff =
        condition.departureRangeEndDate == 0 || condition.departureRangeStartDate == 0
          ? 1
          : condition.departureRangeEndDate - condition.departureRangeStartDate <= 0
          ? 1
          : (condition.departureRangeEndDate - condition.departureRangeStartDate) / 86400000 + 1;
      depart_date_diff = depart_date_diff == 0 ? 1 : depart_date_diff;

      let arrival_date_diff =
        condition.returnRangeEndDate == 0 || condition.returnRangeStartDate == 0
          ? 1
          : condition.returnRangeEndDate - condition.returnRangeStartDate < 0
          ? 1
          : (condition.returnRangeEndDate - condition.returnRangeStartDate) / 86400000;
      arrival_date_diff = arrival_date_diff == 0 ? 1 : arrival_date_diff;

      const dateLength =
        condition.flightWay == FlightWayEnum.OneWay
          ? oneway_date_diff
          : depart_date_diff * arrival_date_diff;

      const toLocationLength = condition.toLocation?.length || 1;
      const fromLocationLength = condition.fromLocation?.length || 1;

      const choice_count_dep = entry_left / (toLocationLength * dateLength);

      // Set arrival count to 1 for round trip and non-range
      let raw_choice_count_arrival = entry_left / (fromLocationLength * dateLength);
      if (condition.flightWay == FlightWayEnum.RoundTrip && !condition.rangeV2 && freeUser) {
        raw_choice_count_arrival = 1;
      }
      const choice_count_arrival = raw_choice_count_arrival;
      const daterange_dep =
        entry_left / (toLocationLength * fromLocationLength) -
        (arrival_date_diff > 0 ? arrival_date_diff : 1) +
        (condition.flightWay == FlightWayEnum.OneWay ? 1 : 0);

      const daterange_arrival =
        entry_left / (toLocationLength * fromLocationLength) -
        (depart_date_diff > 0 ? depart_date_diff : 1) +
        (condition.flightWay == FlightWayEnum.OneWay ? 1 : 0);

      setSearchLimit({
        flight_way: 1,
        choice_count_dep: Math.floor(choice_count_dep) !== 0 ? Math.floor(choice_count_dep) : 1,
        choice_count_arrival:
          Math.floor(choice_count_arrival) !== 0 ? Math.floor(choice_count_arrival) : 1,
        daterange_dep: freeUser
          ? Math.min(maxEntry, Math.floor(daterange_dep))
          : Math.floor(daterange_dep),
        daterange_arrival: freeUser
          ? Math.min(maxEntry, Math.floor(daterange_arrival))
          : Math.floor(daterange_arrival),
        search_calendar_range: !freeUser ? 365 : condition.rangeV2 ? MAX_PRO_DATE_RANGE : 365,
      });
    } catch (error) {
      console.error(error);
    }
    // console.log({
    //   flight_way: 1,
    //   choice_count_dep: Math.floor(choice_count_dep),
    //   choice_count_arrival: Math.floor(choice_count_arrival),
    //   daterange_dep: freeUser ? Math.min(3, Math.floor(daterange_dep)) : Math.floor(daterange_dep),
    //   daterange_arrival: freeUser
    //     ? Math.min(3, Math.floor(daterange_arrival))
    //     : Math.floor(daterange_arrival),
    // });
  }, [condition, store?.user_type]);

  useEffect(() => {
    if (!window.location.search || window.location.search.includes("tab=")) {
      try {
        const data = { ...initParams.current };

        const localDataJson = localStorage.getItem("flight_search_conditions");
        const localData = localDataJson ? JSON.parse(localDataJson) : "";

        if (localData) {
          Object.assign(data, {
            // fromLocation: localData.fromLocation,
            // toLocation: localData.toLocation,
            // cabins: localData.cabins,
            ...localData,
          });
        } else {
          if (
            (data.fromLocation.length == 0 ||
              data.fromLocation[0].iata !== store?.persistStore?.closest_airport) &&
            store?.persistStore?.closest_airport &&
            data.fromLocation.length === 0
          ) {
            Object.assign(data, {
              fromLocation: [{ iata: store.persistStore.closest_airport } as AirportMapItem],
            });
          }

          if (store?.persistStore?.user_preference?.cabins) {
            Object.assign(data, { cabins: store.persistStore.user_preference.cabins });
          } else {
            Object.assign(data, { cabins: "Economy&Premium Economy&Business&First" });
          }
        }
        setCondition(data);
      } catch (err) {
        console.log(err);
      }
    }
  }, [store.persistStore, store?.user_preference?.cabins]);

  const onOneWayDateChange = useEvent((date: Dayjs | null) => {
    const startDate = date ? dayjs(date).valueOf() : 0;

    setCondition((pre) => {
      return { ...pre, oneWayDate: startDate };
    });
  });

  const onOneWayRangeDateChange = useEvent((date: Array<Dayjs | null>) => {
    setCondition((pre) => {
      const oneWayRangeStartDate = date[0] ? dayjs(date[0]).valueOf() : 0;
      const oneWayRangeEndDate = date[1] ? dayjs(date[1]).valueOf() : 0;

      return {
        ...pre,
        oneWayRangeStartDate,
        oneWayRangeEndDate,
        oneWayDate: oneWayRangeStartDate || oneWayRangeEndDate || pre.oneWayDate,
      };
    });
  });

  const onRoundTripDepartureDateChange = useEvent((date: Dayjs | null) => {
    const roundTripDepartureDate = date ? dayjs(date).valueOf() : 0;

    setCondition({
      ...condition,
      roundTripDepartureDate,
      roundTripReturnDate: roundTripDepartureDate,
    });
  });

  const onRoundTripReturnDateChange = useEvent((date: Dayjs | null) => {
    setCondition({
      ...condition,
      roundTripReturnDate: date ? dayjs(date).valueOf() : 0,
    });
  });

  const onDepartureRangeChange = useEvent((date: Array<Dayjs | null>) => {
    setCondition({
      ...condition,
      departureRangeStartDate: date[0] ? dayjs(date[0]).valueOf() : 0,
      departureRangeEndDate: date[1] ? dayjs(date[1]).valueOf() : 0,
    });
  });

  const onReturnRangeChange = useEvent((date: Array<Dayjs | null>) => {
    setCondition({
      ...condition,
      returnRangeStartDate: date[0] ? dayjs(date[0]).valueOf() : 0,
      returnRangeEndDate: date[1] ? dayjs(date[1]).valueOf() : 0,
    });
  });

  const onLocationSwapChange = useEvent(() => {
    setCondition((pre) => {
      return {
        ...pre,
        fromLocation: pre.toLocation,
        toLocation: pre.fromLocation,
      };
    });
  });

  const onSwitchChange = useEvent((val: boolean) => {
    setCondition((pre) => {
      return {
        ...pre,
        rangeV2: val,
      };
    });
  });

  const onProgramsChange = useEvent((val: string[]) => {
    setCondition((pre) => {
      return {
        ...pre,
        programs: val,
      };
    });
  });

  return {
    condition,
    setCondition,
    searchLimit,
    setSearchLimit,
    onLocationSwapChange,
    onSwitchChange,
    onOneWayDateChange,
    onOneWayRangeDateChange,
    onRoundTripDepartureDateChange,
    onRoundTripReturnDateChange,
    onDepartureRangeChange,
    onReturnRangeChange,
    onProgramsChange,
  };
};

export default useSearchCondition;
